import React from "react"

// Input V2 global (drop-in de <input>) — source de vérité du clavier des champs :
//  - Enter : si `nextRef` → focus la cible ; sinon si `onEnter` → onEnter(e) ; sinon rien.
//  - Maj+Enter : si `prevRef` → focus la cible précédente ; sinon rien.
//  - `+`/`-` (pavé num.) UNIQUEMENT pour type="date" : pas de date valide → aujourd'hui ;
//    date valide → +1 / -1 jour. Respecte disabled/readOnly/min/max.
// Toutes les autres props (value, onChange, style, disabled, min, max…) sont transmises
// telles quelles → agnostique du format (chaque site garde sa conversion FR/ISO).

const isPlus = (e) => e.code === "NumpadAdd" || e.key === "+"
const isMinus = (e) => e.code === "NumpadSubtract" || e.key === "-"
const pad = (n) => String(n).padStart(2, "0")
const toISO = (d) =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

// Réinjecte une valeur dans un input contrôlé React via le setter natif + event
// `input` → déclenche le onChange existant du champ (technique standard React 16).
function setNativeValue(el, value) {
	const setter = Object.getOwnPropertyDescriptor(
		window.HTMLInputElement.prototype,
		"value",
	).set
	setter.call(el, value)
	el.dispatchEvent(new Event("input", { bubbles: true }))
}

function focusTarget(nextRef) {
	if (!nextRef) return
	const node = nextRef.current !== undefined ? nextRef.current : nextRef
	if (node && typeof node.focus === "function") node.focus()
}

function stepDate(el, delta) {
	if (el.disabled || el.readOnly) return
	let d
	if (/^\d{4}-\d{2}-\d{2}$/.test(el.value)) {
		d = new Date(`${el.value}T00:00:00`)
		d.setDate(d.getDate() + delta)
	} else {
		d = new Date() // pas de date valide → aujourd'hui
	}
	let iso = toISO(d)
	if (el.min && iso < el.min) iso = el.min
	if (el.max && iso > el.max) iso = el.max
	setNativeValue(el, iso)
}

const InputV2 = React.forwardRef(
	({ type = "text", nextRef, prevRef, onEnter, onKeyDown, ...rest }, ref) => {
		const handleKeyDown = (e) => {
			if (e.key === "Enter") {
				if (e.shiftKey) {
					// Maj+Enter → champ précédent (si fourni).
					if (prevRef) {
						e.preventDefault()
						focusTarget(prevRef)
					}
				} else if (nextRef) {
					e.preventDefault()
					focusTarget(nextRef)
				} else if (onEnter) {
					e.preventDefault()
					onEnter(e)
				}
			} else if (type === "date" && (isPlus(e) || isMinus(e))) {
				e.preventDefault()
				stepDate(e.target, isPlus(e) ? 1 : -1)
			}
			if (onKeyDown) onKeyDown(e)
		}
		return <input ref={ref} type={type} onKeyDown={handleKeyDown} {...rest} />
	},
)

InputV2.displayName = "InputV2"

export default InputV2
